import React, { useState, useEffect } from "react";
import { Modal, ModalBody, CardTitle, Row, Col, Card, CardBody, Input, Button } from "reactstrap";
import { TherapistService } from "../../services/TherapistService";
import { AppointmentService } from "../../services/AppointmentService";
import { Util } from "../../Util";
import moment from "moment";
import { toast } from "react-toastify";

interface ClientPostSessionSurveyProps {
  isOpen: boolean;
  onClose: (surveyData: any, isSkipped: boolean) => void;
  therapistId?: string; // Thêm therapistId để biết therapist nào
  clientId?: string; // Thêm clientId
}

// Hàm helper để lấy số ngày trong tháng
const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month + 1, 0).getDate();
};

// Hàm helper để lấy ngày bắt đầu của tháng (0: Chủ nhật, 1: Thứ 2,...)
const getStartDayOfMonth = (year: number, month: number): number => {
  return new Date(year, month, 1).getDay();
};

const ClientPostSessionSurvey: React.FC<ClientPostSessionSurveyProps> = (props) => {
  const [rating, setRating] = useState(5);
  const [feedback, setFeedback] = useState("");
  const [therapistComfortable, setTherapistComfortable] = useState<string | null>(null);
  const [satisfied, setSatisfied] = useState<string | null>(null);
  const [nextSessionScheduled, setNextSessionScheduled] = useState<string | null>(null);
  const [showContactMessage, setShowContactMessage] = useState<boolean>(false);
  const [clientId, setClientId] = useState<string>("");
  const [isCreatingAppointment, setIsCreatingAppointment] = useState<boolean>(false);

  // Thêm state để quản lý lịch
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [monthName, setMonthName] = useState("");

  // State mới cho available slots của therapist
  const [therapistId, setTherapistId] = useState<string>("");
  const [therapistAvailableHours, setTherapistAvailableHours] = useState<any[]>([]);
  const [blockDatesListForSelectedMonth, setBlockDatesListForSelectedMonth] = useState<any[]>([]);
  const [appointmentListForSelectedMonth, setAppointmentListForSelectedMonth] = useState<any[]>([]);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState<boolean>(false);
  const [therapistData, setTherapistData] = useState<any>(null);
  const [allBlockDatesListInTherapist, setAllBlockDatesListInTherapist] = useState<any[]>([]);
  const [allAppointmentListInTherapist, setAllAppointmentListInTherapist] = useState<any[]>([]);

  // Checkbox states for agreements
  const [agreedToDisclosure, setAgreedToDisclosure] = useState(false);
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);
  const [agreedToConsent, setAgreedToConsent] = useState(false);

  // Mảng tên tháng
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Cập nhật tên tháng khi currentMonth thay đổi
  useEffect(() => {
    setMonthName(monthNames[currentMonth]);
  }, [currentMonth]);

  // Lấy dữ liệu therapist khi component được mở
  useEffect(() => {
    if (props.isOpen && props.therapistId) {
      setTherapistId(props.therapistId);

      // Sử dụng clientId từ props nếu có
      if (props.clientId) {
        setClientId(props.clientId);
      } else {
        // Fallback: Lấy clientId từ localStorage nếu không có trong props
        const userData = localStorage.getItem('user');
        if (userData) {
          try {
            const parsedUserData = JSON.parse(userData);
            if (parsedUserData && parsedUserData._id) {
              setClientId(parsedUserData._id);
            }
          } catch (error) {
            console.error('Error parsing user data:', error);
          }
        }
      }
    }
  }, [props.isOpen, props.therapistId, props.clientId]);

  // Gọi API lấy availability khi therapistId thay đổi
  useEffect(() => {
    if (therapistId) {
      getTherapistAvailability();
    }
  }, [therapistId]);

  // Gọi API lấy lịch cho tháng cụ thể khi tháng thay đổi
  useEffect(() => {
    if (therapistId && therapistData) {
      selectAnotherMonthAndRefreshAppointmentList(new Date(currentYear, currentMonth, 1));
    }
  }, [therapistId, currentMonth, currentYear, therapistData]);

  // Hàm chuyển đổi ngày trong tuần sang số
  const dayOfWeekAsNumber = (day: string): number => {
    const dayMap: Record<string, number> = {
      'Sunday': 0,
      'Monday': 1,
      'Tuesday': 2,
      'Wednesday': 3,
      'Thursday': 4,
      'Friday': 5,
      'Saturday': 6
    };
    return dayMap[day];
  };

  // Hàm lấy thông tin lịch rảnh của therapist - UPDATED từ CustomModal.js
  const getTherapistAvailability = async () => {
    if (!therapistId) return;

    try {
      setIsLoadingAvailability(true);
      const res = await TherapistService.getTherapistDetailsbyIdPublic(therapistId);

      if (res?.success && res.data) {
        setTherapistData(res.data);
        const availableDays: any[] = [];

        if (res.data.workingHours?.length) {
          res.data.workingHours.forEach((obj: any) => {
            const dayAsNumber = dayOfWeekAsNumber(obj.day);

            if (Util.convertUTCDateToLocalDate(obj.endTime) > Util.convertUTCDateToLocalDate(obj.startTime)) {
              availableDays.push({
                startTime: Util.convertUTCDateToLocalDate(obj.startTime),
                endTime: Util.convertUTCDateToLocalDate(obj.endTime),
                daysOfWeek: [dayAsNumber],
              });
            } else {
              availableDays.push({
                startTime: Util.convertUTCDateToLocalDate(obj.startTime),
                endTime: "24:00 AM",
                daysOfWeek: [dayAsNumber - 1],
              });

              availableDays.push({
                startTime: "00:00 AM",
                endTime: Util.convertUTCDateToLocalDate(obj.endTime),
                daysOfWeek: [dayAsNumber],
              });
            }
          });
        } else {
          availableDays.push({
            startTime: "00:00 AM",
            endTime: "00:00 PM",
            daysOfWeek: [10], // Một ngày không tồn tại
          });
        }

        setTherapistAvailableHours(availableDays);

        // Lưu trữ toàn bộ blocked dates
        const blockedDates = res.data.blockedDates || [];
        setAllBlockDatesListInTherapist(blockedDates);

        // Lấy danh sách cuộc hẹn đã đặt cho tháng hiện tại
        const selectedDate = new Date(currentYear, currentMonth, 1);
        const appointments = await AppointmentService.getAllAppointmentByTherapistIdPublicForMonth(therapistId, selectedDate);

        if (appointments?.success) {
          // Chuyển dates thành Date objects - quan trọng để logic hoạt động đúng
          const updatedAppointments = appointments.data.map((appointment: any) => ({
            ...appointment,
            start: new Date(appointment.start),
            end: new Date(appointment.end)
          }));

          // Lưu toàn bộ danh sách cuộc hẹn
          setAllAppointmentListInTherapist(updatedAppointments);

          // Lọc cho tháng hiện tại
          setArraysOnlyForSelectedMonth(selectedDate, blockedDates, updatedAppointments);
        }
      }
    } catch (error) {
      console.error('Error getting therapist data:', error);
      toast.error("Unable to load therapist schedule");
    } finally {
      setIsLoadingAvailability(false);
    }
  };

  // Hàm lọc blocked dates và appointments cho tháng được chọn - từ CustomModal.js
  const setArraysOnlyForSelectedMonth = (monthForCheckFrom: Date, allBlockedSlotsFrom: any[], allAppointmentsFrom: any[]) => {
    const monthForCheck = moment(monthForCheckFrom).month();
    const yearForCheck = moment(monthForCheckFrom).year();

    if (allBlockedSlotsFrom) {
      const filteredBlockedDatesForThisMonth = allBlockedSlotsFrom.filter((item: any) => {
        const startMonth = moment(item.start).month();
        const startYear = moment(item.start).year();
        const endMonth = moment(item.end).month();
        const endYear = moment(item.end).year();

        return (startMonth === monthForCheck && startYear === yearForCheck) ||
               (endMonth === monthForCheck && endYear === yearForCheck);
      });

      setBlockDatesListForSelectedMonth(filteredBlockedDatesForThisMonth);
    }

    if (allAppointmentsFrom) {
      const filteredAppointmentsForThisMonth = allAppointmentsFrom.filter((item: any) => {
        const startMonth = moment(item.start).month();
        const startYear = moment(item.start).year();
        const endMonth = moment(item.end).month();
        const endYear = moment(item.end).year();

        return (startMonth === monthForCheck && startYear === yearForCheck) ||
               (endMonth === monthForCheck && endYear === yearForCheck);
      });
      setAppointmentListForSelectedMonth(filteredAppointmentsForThisMonth);
    }
  };

  // Hàm cập nhật dữ liệu khi chọn tháng khác - từ CustomModal.js
  const selectAnotherMonthAndRefreshAppointmentList = (selectedMonthIn: Date) => {
    try {
      if (therapistData && therapistId) {
        setIsLoadingAvailability(true);
        setArraysOnlyForSelectedMonth(selectedMonthIn, allBlockDatesListInTherapist, allAppointmentListInTherapist);
        setIsLoadingAvailability(false);
      }
    } catch (error) {
      setIsLoadingAvailability(false);
      console.error("Error refreshing appointment list:", error);
    }
  };

  // Lấy các ngày có sẵn cho tháng hiện tại - đồng bộ với logic trong CustomAppointmentCalendar.tsx
  const getAvailableDays = (): number[] => {
    // Nếu đang loading hoặc không có therapistId, trả về mảng rỗng
    if (isLoadingAvailability || !therapistId) {
      return [];
    }

    const availableDays: number[] = [];
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const todayDate: Date = new Date();
    const tomorrowDate: Date = moment(todayDate).add(1, "day").startOf("day").toDate();

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);

      // Chỉ kiểm tra các ngày trong tương lai
      if (moment(date).isBefore(tomorrowDate)) {
        continue;
      }

      // Kiểm tra có time slots available thật sự không - key point để sửa lỗi ngày 15
      const dayOfWeek = date.getDay();

      // Tạo thử các time slots và kiểm tra sau khi lọc còn bao nhiêu
      const availableHours: string[][] = [];

      therapistAvailableHours
        .filter((obj) => obj.daysOfWeek[0] === dayOfWeek)
        .forEach((obj) => {
          const availableHourSingle: string[] = [];

          const startHourMatch = obj.startTime.match(/(\d+):/);
          const endHourMatch = obj.endTime.match(/(\d+):/);

          if (startHourMatch && endHourMatch) {
            const startHour = parseInt(startHourMatch[1]);
            const endHour = parseInt(endHourMatch[1]);

            for (let hour = startHour; hour <= endHour; hour++) {
              if (hour === startHour) {
                const mST = moment(obj.startTime, "HH:mm").minute();
                if (mST === 0) {
                  availableHourSingle.push(moment({ hour }).format("h:mm A"));
                }
              } else {
                availableHourSingle.push(moment({ hour }).format("h:mm A"));
              }

              if (hour !== endHour) {
                availableHourSingle.push(
                  moment({
                    hour,
                    minute: 30,
                  }).format("h:mm A")
                );
              } else {
                const mET = moment(obj.endTime, "HH:mm").minute();
                if (mET === 30) {
                  availableHourSingle.push(
                    moment({
                      hour,
                      minute: 30,
                    }).format("h:mm A")
                  );
                }
              }
            }
          }

          availableHours.push(availableHourSingle);
        });

      // Tạo tất cả time slots để kiểm tra có slots nào thật sự available không
      let allAvailableSlots: string[] = [];

      availableHours.forEach((slotArray: string[]) => {
        slotArray.forEach((slot: string) => {
          const slotTime = moment(slot, "h:mm A");
          const slotDate = moment(date).hours(slotTime.hours()).minutes(slotTime.minutes()).seconds(0);
          const endTime = moment(slotDate).add(60, "minutes");

          if (
            slotArray.includes(moment(slotDate).format("h:mm A")) &&
            slotArray.includes(moment(endTime).format("h:mm A"))
          ) {
            allAvailableSlots.push(slot);
          }
        });
      });

      // Loại bỏ các slot trùng lặp
      allAvailableSlots = [...new Set(allAvailableSlots)];

      // Lọc các slot bị block và đã có appointment, giống như trong getAvailableTimesForSelectedDate()
      allAvailableSlots = allAvailableSlots.filter(slot => {
        const slotTime = moment(slot, "h:mm A");
        const slotDate = moment(date).hours(slotTime.hours()).minutes(slotTime.minutes()).seconds(0);

        // Kiểm tra xem slot có nằm trong blocked dates không
        const isInBlockedRange = blockDatesListForSelectedMonth.some((range: any) => {
          return Util.isDateInBlockedRange(moment(range.start), moment(range.end), slotDate);
        });

        if (isInBlockedRange) {
          return false;
        }

        // Kiểm tra xem slot đã có hẹn chưa
        const appointmentAlreadyExists = appointmentListForSelectedMonth.some((appointment: any) => {
          return Util.isDateInBlockedRange(moment(appointment.start), moment(appointment.end), slotDate);
        });

        return !isInBlockedRange && !appointmentAlreadyExists;
      });

      // Chỉ có ngày có ít nhất 1 slot available mới được coi là ngày available
      if (allAvailableSlots.length > 0) {
        availableDays.push(day);
      }
    }

    return availableDays;
  };

  // Hàm lấy các khung giờ khả dụng cho ngày được chọn - từ CustomModal.js
  // const getAvailableTimeBySelectedDay = (dayNumber: number, availableHours: any[]): string[][] => {
  //   const availableTimeSlots: string[][] = [];

  //   availableHours
  //     .filter((obj) => obj.daysOfWeek[0] === dayNumber)
  //     .forEach((obj) => {
  //       const availableHourSingle: string[] = [];

  //       // Extract hours from startTime and endTime
  //       const startHourMatch = obj.startTime.match(/(\d+):/);
  //       const endHourMatch = obj.endTime.match(/(\d+):/);

  //       if (startHourMatch && endHourMatch) {
  //         const startHour = parseInt(startHourMatch[1]);
  //         const endHour = parseInt(endHourMatch[1]);

  //         for (let hour = startHour; hour <= endHour; hour++) {
  //           if (hour === startHour) {
  //             const mST = moment(obj.startTime, "HH:mm").minute();
  //             if (mST === 0) {
  //               availableHourSingle.push(moment({ hour }).format("h:mm A"));
  //             }
  //           } else {
  //             availableHourSingle.push(moment({ hour }).format("h:mm A"));
  //           }

  //           if (hour !== endHour) {
  //             availableHourSingle.push(
  //               moment({
  //                 hour,
  //                 minute: 30,
  //               }).format("h:mm A")
  //             );
  //           } else {
  //             const mET = moment(obj.endTime, "HH:mm").minute();
  //             if (mET === 30) {
  //               availableHourSingle.push(
  //                 moment({
  //                   hour,
  //                   minute: 30,
  //                 }).format("h:mm A")
  //               );
  //             }
  //           }
  //         }
  //       }

  //       availableTimeSlots.push(availableHourSingle);
  //     });

  //   return availableTimeSlots;
  // };

  // Lấy available times cho ngày đã chọn - UPDATED để khớp với PublicAppointmentCalendar.tsx
  const getAvailableTimesForSelectedDate = (): string[] => {
    if (!selectedDate) return [];

    const date = new Date(currentYear, currentMonth, selectedDate);
    const dayOfWeek = date.getDay();

    // Tương tự với code trong PublicAppointmentCalendar.tsx
    const availableHours: string[][] = [];

    therapistAvailableHours
      .filter((obj) => obj.daysOfWeek[0] === dayOfWeek)
      .forEach((obj) => {
        const availableHourSingle: string[] = [];

        // Extract hours from startTime and endTime
        const startHourMatch = obj.startTime.match(/(\d+):/);
        const endHourMatch = obj.endTime.match(/(\d+):/);

        if (startHourMatch && endHourMatch) {
          const startHour = parseInt(startHourMatch[1]);
          const endHour = parseInt(endHourMatch[1]);

          for (let hour = startHour; hour <= endHour; hour++) {
            if (hour === startHour) {
              const mST = moment(obj.startTime, "HH:mm").minute();
              if (mST === 0) {
                availableHourSingle.push(moment({ hour }).format("h:mm A"));
              }
            } else {
              availableHourSingle.push(moment({ hour }).format("h:mm A"));
            }

            if (hour !== endHour) {
              availableHourSingle.push(
                moment({
                  hour,
                  minute: 30,
                }).format("h:mm A")
              );
            } else {
              const mET = moment(obj.endTime, "HH:mm").minute();
              if (mET === 30) {
                availableHourSingle.push(
                  moment({
                    hour,
                    minute: 30,
                  }).format("h:mm A")
                );
              }
            }
          }
        }

        availableHours.push(availableHourSingle);
      });

    let allAvailableSlots: string[] = [];

    // Tạo các time slots đầy đủ
    availableHours.forEach((slotArray: string[]) => {
      slotArray.forEach((slot: string) => {
        const slotTime = moment(slot, "h:mm A");
        const slotDate = moment(date).hours(slotTime.hours()).minutes(slotTime.minutes()).seconds(0);
        const endTime = moment(slotDate).add(60, "minutes");

        // Kiểm tra xem cả slot bắt đầu và kết thúc có nằm trong working hours không
        if (
          moment(date).isAfter(moment().startOf('day')) && // Chỉ lấy các ngày từ ngày mai
          slotArray.includes(moment(slotDate).format("h:mm A")) &&
          slotArray.includes(moment(endTime).format("h:mm A"))
        ) {
          allAvailableSlots.push(slot);
        }
      });
    });

    // Loại bỏ các slot trùng lặp
    allAvailableSlots = [...new Set(allAvailableSlots)];

    // Quan trọng: Lọc các slot nằm trong blocked dates
    allAvailableSlots = allAvailableSlots.filter(slot => {
      const slotTime = moment(slot, "h:mm A");
      const slotDate = moment(date).hours(slotTime.hours()).minutes(slotTime.minutes()).seconds(0);

      // Kiểm tra xem slot có nằm trong blocked dates không
      const isInBlockedRange = blockDatesListForSelectedMonth.some((range: any) => {
        return Util.isDateInBlockedRange(moment(range.start), moment(range.end), slotDate);
      });

      if (isInBlockedRange) {
        return false;
      }

      // Kiểm tra xem slot đã có hẹn chưa
      const appointmentAlreadyExists = appointmentListForSelectedMonth.some((appointment: any) => {
        return Util.isDateInBlockedRange(moment(appointment.start), moment(appointment.end), slotDate);
      });

      return !isInBlockedRange && !appointmentAlreadyExists;
    });

    // Sắp xếp các slots theo thời gian
    allAvailableSlots.sort((a, b) => {
      return moment(a, "h:mm A").valueOf() - moment(b, "h:mm A").valueOf();
    });

    return allAvailableSlots;
  };

  const handleRating = (newRating: number) => {
    setRating(newRating);
  };

  const handleFeedbackChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFeedback(e.target.value);
  };

  const [currentTimeSlotPage, setCurrentTimeSlotPage] = useState<number>(1);
  const [displayMode, setDisplayMode] = useState<'paginated' | 'showMore'>('paginated');
  const [showAllTimeSlots, setShowAllTimeSlots] = useState<boolean>(false);
  const TIME_SLOTS_PER_PAGE = 8; // Số time slots hiển thị trên mỗi trang

  const handleDateSelect = (day: number) => {
    setSelectedDate(day);
    setSelectedTime(null); // Reset selected time when date changes
    setCurrentTimeSlotPage(1); // Reset về trang đầu tiên khi chọn ngày mới
    setShowAllTimeSlots(false); // Reset show all khi chọn ngày mới
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
  };

  // Hàm để tạo cuộc hẹn
  const createAppointment = async () => {
    if (!selectedDate || !selectedTime || !therapistId) {
      toast.error("Please select a date and time before scheduling");
      return;
    }

    setIsCreatingAppointment(true);

    try {
      // Tạo đối tượng ngày từ ngày và giờ đã chọn
      const selectedDateTime = moment(new Date(currentYear, currentMonth, selectedDate));

      // Xử lý an toàn với selectedTime
      const timeStr = selectedTime as string; // Ép kiểu thành string vì đã kiểm tra null ở trên
      const timeMatch = timeStr.match(/(\d+):(\d+)\s(AM|PM)/);

      if (!timeMatch) {
        toast.error("Invalid time format");
        setIsCreatingAppointment(false);
        return;
      }

      const [hour, minute, period] = timeMatch.slice(1);
      let hourNumber = parseInt(hour);

      // Chuyển đổi giờ sang định dạng 24 giờ
      if (period === "PM" && hourNumber < 12) {
        hourNumber += 12;
      } else if (period === "AM" && hourNumber === 12) {
        hourNumber = 0;
      }

      // Đặt giờ và phút cho ngày đã chọn
      selectedDateTime.set({
        hour: hourNumber,
        minute: parseInt(minute),
        second: 0,
        millisecond: 0
      });

      // Tạo thời gian kết thúc (1 giờ sau thời gian bắt đầu)
      const endDateTime = moment(selectedDateTime).add(1, 'hour');

      // Tạo đối tượng dữ liệu cuộc hẹn
      const appointmentData = {
        therapistId: therapistId,
        clientId: clientId, // Sử dụng clientId từ state
        title: "Lavni Therapy session",
        type: "Video - 1 Hour Session",
        start: selectedDateTime.toISOString(),
        end: endDateTime.toISOString(),
        reminders: [30],
        repeatInfo: {
          repeatType: "DOES NOT REPEAT",
          interval: "",
          repeatDays: {
            sunday: false,
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false
          },
          endingDate: endDateTime.toISOString(),
          endingAfter: 10,
          endingType: ""
        },
        color: "#FF6900",
        groupId: `${therapistId}_${selectedDateTime.valueOf()}`,
        eventOnHolidays: false
      };

      // Gọi API để tạo cuộc hẹn - service sẽ tự động bọc vào { appointment: ... }
      const response = await AppointmentService.createAppointmentByClient(appointmentData);

      if (response.success) {
        toast.success("Appointment scheduled successfully!");

        const surveyData = {
          rating,
          feedback,
          therapistComfortable: therapistComfortable === "YES",
          satisfied: satisfied === "YES",
          nextSessionScheduled: true,
          needCall: satisfied === "CONTACT",
          scheduledDate: `${monthName} ${selectedDate}, ${currentYear} at ${selectedTime}`
        };

        props.onClose(surveyData, false);
      } else {
        toast.error(response.message || "Unable to schedule appointment. Please try again later.");
      }
    } catch (error) {
      console.error("Error creating appointment:", error);
      toast.error("An error occurred while scheduling the appointment. Please try again later.");
    } finally {
      setIsCreatingAppointment(false);
    }
  };

  // Thêm hàm xử lý submit feedback
  const handleSubmitFeedback = () => {
    let isSatisfied = null;
    let needCall = false;

    if (satisfied === "YES") {
      isSatisfied = true;
    } else if (satisfied === "CONTACT") {
      isSatisfied = false;
      needCall = true;
    } else if (satisfied === "NO") {
      isSatisfied = false;
    }

    // Tạo dữ liệu feedback
    const surveyData = {
      rating,
      feedback,
      therapistComfortable: therapistComfortable === "YES",
      satisfied: isSatisfied,
      nextSessionScheduled: nextSessionScheduled === "YES",
      needCall: needCall,
      scheduledDate: selectedDate && selectedTime ?
        `${monthName} ${selectedDate}, ${currentYear} at ${selectedTime}` : undefined
    };

    // Đóng modal và trả về dữ liệu feedback
    props.onClose(surveyData, false);
  };

  // Hàm chuyển tháng
  const changeMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentYear, currentMonth, 1);

    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }

    setCurrentMonth(newDate.getMonth());
    setCurrentYear(newDate.getFullYear());

    // Reset ngày được chọn khi chuyển tháng
    setSelectedDate(null);
    setSelectedTime(null);
  };

  // Hàm để chuyển đổi chế độ hiển thị (phân trang hoặc show more)
  const toggleDisplayMode = () => {
    setDisplayMode(prev => prev === 'paginated' ? 'showMore' : 'paginated');
    setCurrentTimeSlotPage(1);
    setShowAllTimeSlots(false);
  };

  // Tạo lưới lịch
  const renderCalendar = () => {
    const daysInWeek = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
    const totalDays = getDaysInMonth(currentYear, currentMonth);
    const startDay = getStartDayOfMonth(currentYear, currentMonth);
    const availableDays = getAvailableDays();

    // Render header ngày trong tuần
    const weekdayHeader = (
      <Row className="text-center mb-2">
        {daysInWeek.map(day => (
          <Col key={day} className="p-0">
            <div style={{
              fontWeight: "600",
              color: "#555",
              fontSize: "12px"
            }}>
              {day}
            </div>
          </Col>
        ))}
      </Row>
    );

    // Render các ngày trong tháng
    const renderDays = () => {
      const rows = [];
      let cells = [];

      // Thêm các ô trống cho ngày đầu tháng
      for (let i = 0; i < startDay; i++) {
        cells.push(<Col key={`empty-start-${i}`} className="text-center p-0"></Col>);
      }

      // Tạo mảng tất cả các ngày trong tháng
      for (let i = 1; i <= totalDays; i++) {
        const isAvailable = availableDays.includes(i);
        const isSelected = selectedDate === i;

        cells.push(
          <Col key={i} className="text-center p-0 mb-1">
            <div
              onClick={() => isAvailable ? handleDateSelect(i) : null}
              style={{
                width: "100%",
                height: "30px",
                borderRadius: "50%",
                margin: "0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: isAvailable ? "pointer" : "default",
                backgroundColor: isSelected ? "#F47B20" : (isAvailable ? "#FFF2E3" : "transparent"),
                color: isSelected ? "white" : (isAvailable ? "#333" : "#aaa"),
                fontSize: "14px",
                fontWeight: isSelected ? "bold" : "normal",
                transition: "all 0.2s ease",
                boxShadow: isSelected ? "0px 2px 5px rgba(244, 123, 32, 0.3)" : "none",
                border: isAvailable && !isSelected ? "1px solid #FFE8D1" : "none",
                maxWidth: "30px"
              }}
              onMouseEnter={(e) => {
                if (isAvailable && !isSelected) {
                  e.currentTarget.style.backgroundColor = "#FFEACB";
                  e.currentTarget.style.boxShadow = "0px 2px 5px rgba(244, 123, 32, 0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (isAvailable && !isSelected) {
                  e.currentTarget.style.backgroundColor = "#FFF2E3";
                  e.currentTarget.style.boxShadow = "none";
                }
              }}
            >
              {i}
            </div>
          </Col>
        );

        // Mỗi hàng có 7 ô (7 ngày), khi đến ô thứ 7 hoặc ngày cuối tháng thì tạo hàng mới
        if ((cells.length) % 7 === 0 || i === totalDays) {
          // Nếu hàng cuối không đủ 7 ô, thêm các ô trống
          while (cells.length % 7 !== 0) {
            cells.push(<Col key={`empty-end-${cells.length}`} className="text-center p-0"></Col>);
          }

          rows.push(<Row key={`row-${Math.ceil(i/7)}`} className="g-0 mb-1">{cells}</Row>);
          cells = [];
        }
      }

      return rows;
    };

    return (
      <div className="calendar-container p-2 p-md-3 mb-3" style={{
        backgroundColor: "#FAFAFA",
        borderRadius: "12px",
        boxShadow: "0px 3px 10px rgba(0, 0, 0, 0.05)"
      }}>
        <div className="text-center mb-3" style={{
          color: "#8B4513",
          fontWeight: "bold",
          fontSize: "16px"
        }}>
          Schedule Your Next Session
        </div>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <span
            onClick={() => changeMonth('prev')}
            style={{
              cursor: "pointer",
              color: "#F47B20",
              width: "28px",
              height: "28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#FFF2E3",
              borderRadius: "50%",
              fontSize: "16px",
              fontWeight: "bold"
            }}
          >
            &lt;
          </span>
          <h5 className="m-0" style={{
            color: "#333",
            fontWeight: "600",
            fontSize: "16px"
          }}>
            {`${monthName} ${currentYear}`}
          </h5>
          <span
            onClick={() => changeMonth('next')}
            style={{
              cursor: "pointer",
              color: "#F47B20",
              width: "28px",
              height: "28px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#FFF2E3",
              borderRadius: "50%",
              fontSize: "16px",
              fontWeight: "bold"
            }}
          >
            &gt;
          </span>
        </div>

        {isLoadingAvailability ? (
          <div className="text-center p-2">
            <p>Loading available dates...</p>
          </div>
        ) : (
          <div className="calendar-grid mx-auto" style={{ maxWidth: "100%", overflow: "hidden" }}>
            {weekdayHeader}
            {renderDays()}
          </div>
        )}

        {selectedDate && (
          <div className="available-times mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 style={{
                color: "#8B4513",
                fontWeight: "600",
                fontSize: "14px",
                margin: 0
              }}>
                Available Times
              </h6>

              <Button
                size="sm"
                color="link"
                className="p-0"
                onClick={toggleDisplayMode}
                style={{
                  color: "#F47B20",
                  fontSize: "12px",
                  textDecoration: "none"
                }}
              >
                {displayMode === 'paginated' ? "Use Show More" : "Use Pagination"}
              </Button>
            </div>

            {isLoadingAvailability ? (
              <div className="text-center">Loading available times...</div>
            ) : (
              renderTimeSlots()
            )}

            {/* Agreement Checkboxes */}
            {selectedDate && selectedTime && (
              <div style={{
                marginTop: "16px",
                marginBottom: "16px",
                padding: "12px",
                backgroundColor: "#f8f9fa",
                borderRadius: "6px",
                border: "1px solid #e9ecef"
              }}>
                {/* Custom checkbox #1 - Disclosure Statement */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginBottom: "12px",
                    cursor: "pointer",
                    padding: "8px",
                    backgroundColor: "white",
                    borderRadius: "4px",
                    border: "1px solid #dee2e6",
                    transition: "all 0.2s ease",
                  }}
                  onClick={() => {
                    setAgreedToDisclosure(!agreedToDisclosure);
                  }}
                >
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      border: "2px solid #ccc",
                      borderRadius: 3,
                      marginRight: 10,
                      marginTop: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: agreedToDisclosure ? "#F47B20" : "white",
                      borderColor: agreedToDisclosure ? "#F47B20" : "#ccc",
                      flexShrink: 0
                    }}
                  >
                    {agreedToDisclosure && <i className="bx bx-check" style={{ color: "white", fontSize: 12 }}></i>}
                  </div>
                  <div style={{ color: "#495057", fontSize: 13, lineHeight: "1.3" }}>
                    I agree to <span
                      style={{
                        color: "#F47B20",
                        fontWeight: 600,
                        cursor: "pointer"
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`/api/public/disclosure-statement-of-therapist/${therapistId}`, "_blank");
                      }}
                    >Disclosure Statement</span> of the therapist.
                  </div>
                </div>

                {/* Custom checkbox #2 - No Show and Cancellation Policy */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginBottom: "12px",
                    cursor: "pointer",
                    padding: "8px",
                    backgroundColor: "white",
                    borderRadius: "4px",
                    border: "1px solid #dee2e6",
                    transition: "all 0.2s ease",
                  }}
                  onClick={() => {
                    setAgreedToPolicy(!agreedToPolicy);
                  }}
                >
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      border: "2px solid #ccc",
                      borderRadius: 3,
                      marginRight: 10,
                      marginTop: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: agreedToPolicy ? "#F47B20" : "white",
                      borderColor: agreedToPolicy ? "#F47B20" : "#ccc",
                      flexShrink: 0
                    }}
                  >
                    {agreedToPolicy && <i className="bx bx-check" style={{ color: "white", fontSize: 12 }}></i>}
                  </div>
                  <div style={{ color: "#495057", fontSize: 13, lineHeight: "1.3" }}>
                    I agree to <span
                      style={{
                        color: "#F47B20",
                        fontWeight: 600,
                        cursor: "pointer"
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open("/noshow-policy/", "_blank");
                      }}
                    >No Show and Cancellation Policy</span> of Lavni Inc.
                  </div>
                </div>

                {/* Custom checkbox #3 - Consent Document */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    cursor: "pointer",
                    padding: "8px",
                    backgroundColor: "white",
                    borderRadius: "4px",
                    border: "1px solid #dee2e6",
                    transition: "all 0.2s ease",
                  }}
                  onClick={() => {
                    setAgreedToConsent(!agreedToConsent);
                  }}
                >
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      border: "2px solid #ccc",
                      borderRadius: 3,
                      marginRight: 10,
                      marginTop: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: agreedToConsent ? "#F47B20" : "white",
                      borderColor: agreedToConsent ? "#F47B20" : "#ccc",
                      flexShrink: 0
                    }}
                  >
                    {agreedToConsent && <i className="bx bx-check" style={{ color: "white", fontSize: 12 }}></i>}
                  </div>
                  <div style={{ color: "#495057", fontSize: 13, lineHeight: "1.3" }}>
                    I agree to <span
                      style={{
                        color: "#F47B20",
                        fontWeight: 600,
                        cursor: "pointer"
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open("http://localhost:3000/consent-document/", "_blank");
                      }}
                    >Consent Document</span> of Lavni Inc.
                  </div>
                </div>
              </div>
            )}

            {/* Nút Create Appointment */}
            {selectedDate && selectedTime && (
              <div className="text-center mt-3">
                <Button
                  color="success"
                  onClick={createAppointment}
                  disabled={isCreatingAppointment || !agreedToDisclosure || !agreedToPolicy || !agreedToConsent}
                  style={{
                    backgroundColor: (agreedToDisclosure && agreedToPolicy && agreedToConsent) ? "#32CD32" : "#cccccc",
                    borderColor: (agreedToDisclosure && agreedToPolicy && agreedToConsent) ? "#32CD32" : "#cccccc",
                    color: "white",
                    fontWeight: "600",
                    borderRadius: "8px",
                    padding: "8px 16px",
                    transition: "all 0.2s ease",
                    boxShadow: (agreedToDisclosure && agreedToPolicy && agreedToConsent) ? "0px 2px 5px rgba(50, 205, 50, 0.3)" : "none",
                    cursor: (isCreatingAppointment || !agreedToDisclosure || !agreedToPolicy || !agreedToConsent) ? "not-allowed" : "pointer"
                  }}
                >
                  {isCreatingAppointment ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                      {" "}Processing...
                    </>
                  ) : (
                    "Create Appointment"
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render các time slots theo trang hoặc Show More
  const renderTimeSlots = () => {
    const availableTimes = getAvailableTimesForSelectedDate();
    if (!availableTimes.length) return <div className="text-center">No available times for this date</div>;

    let displayedTimes: string[] = [];

    if (displayMode === 'paginated') {
      // Hiển thị theo trang
      const startIndex = (currentTimeSlotPage - 1) * TIME_SLOTS_PER_PAGE;
      const endIndex = startIndex + TIME_SLOTS_PER_PAGE;
      displayedTimes = availableTimes.slice(startIndex, endIndex);
    } else {
      // Hiển thị theo Show More
      displayedTimes = showAllTimeSlots ? availableTimes : availableTimes.slice(0, TIME_SLOTS_PER_PAGE);
    }

    return (
      <>
        <Row className="g-2">
          {displayedTimes.map((time) => (
            <Col xs="6" key={time} className="mb-2">
              <Button
                size="sm"
                block
                outline
                onClick={() => handleTimeSelect(time)}
                style={{
                  borderColor: selectedTime === time ? "#F47B20" : "#dee2e6",
                  backgroundColor: selectedTime === time ? "#FFF8EF" : "white",
                  color: selectedTime === time ? "#F47B20" : "#555",
                  borderRadius: "8px",
                  padding: "8px 5px",
                  fontWeight: selectedTime === time ? "600" : "normal",
                  transition: "all 0.2s ease",
                  boxShadow: selectedTime === time ? "0px 2px 5px rgba(244, 123, 32, 0.2)" : "none",
                  fontSize: "12px"
                }}
                className="d-flex align-items-center justify-content-center"
              >
                <span style={{
                  marginRight: "5px",
                  color: selectedTime === time ? "#F47B20" : "#888"
                }}>&#128339;</span>
                {time}
              </Button>
            </Col>
          ))}
        </Row>

        {displayMode === 'paginated' && (
          <div className="d-flex justify-content-center mt-3">
            <div className="pagination-controls d-flex align-items-center">
              <Button
                size="sm"
                outline
                onClick={() => setCurrentTimeSlotPage(prev => Math.max(prev - 1, 1))}
                disabled={currentTimeSlotPage === 1}
                style={{
                  borderColor: "#F47B20",
                  color: "#F47B20",
                  borderRadius: "8px",
                  padding: "4px 8px",
                  marginRight: "8px",
                  opacity: currentTimeSlotPage === 1 ? 0.6 : 1
                }}
              >
                &lt;
              </Button>

              <small style={{ color: "#8B4513", fontWeight: "500" }}>
                {currentTimeSlotPage} / {Math.ceil(availableTimes.length / TIME_SLOTS_PER_PAGE)}
              </small>

              <Button
                size="sm"
                outline
                onClick={() => setCurrentTimeSlotPage(prev =>
                  Math.min(prev + 1, Math.ceil(availableTimes.length / TIME_SLOTS_PER_PAGE))
                )}
                disabled={currentTimeSlotPage >= Math.ceil(availableTimes.length / TIME_SLOTS_PER_PAGE)}
                style={{
                  borderColor: "#F47B20",
                  color: "#F47B20",
                  borderRadius: "8px",
                  padding: "4px 8px",
                  marginLeft: "8px",
                  opacity: currentTimeSlotPage >= Math.ceil(availableTimes.length / TIME_SLOTS_PER_PAGE) ? 0.6 : 1
                }}
              >
                &gt;
              </Button>
            </div>
          </div>
        )}

        {displayMode === 'showMore' && availableTimes.length > TIME_SLOTS_PER_PAGE && (
          <div className="text-center mt-3">
            <Button
              size="sm"
              outline
              onClick={() => setShowAllTimeSlots(!showAllTimeSlots)}
              style={{
                borderColor: "#F47B20",
                color: "#F47B20",
                borderRadius: "8px",
                padding: "4px 12px"
              }}
            >
              {showAllTimeSlots ? "Show Less" : `Show More (${availableTimes.length - TIME_SLOTS_PER_PAGE} more)`}
            </Button>
          </div>
        )}
      </>
    );
  };

  // Style cho nút submit
  const submitButtonStyle = {
    backgroundColor: "#F47B20",
    borderColor: "#F47B20",
    color: "white",
    fontWeight: "bold" as "bold",
    padding: "12px",
    fontSize: "16px",
    boxShadow: "0px 4px 10px rgba(244, 123, 32, 0.4)",
    transition: "all 0.3s ease"
  };

  // Style hover cho nút submit
  const [isHovered, setIsHovered] = useState(false);

  const submitButtonHoverStyle = {
    ...submitButtonStyle,
    backgroundColor: "#E06100",
    borderColor: "#E06100",
    transform: "translateY(-2px)",
    boxShadow: "0px 6px 12px rgba(244, 123, 32, 0.5)",
  };

  return (
    <Modal isOpen={props.isOpen} centered className="client-survey-modal" size="md">
      <ModalBody className="p-4">
        <Card className="border-0">
          <CardBody>
            <CardTitle className="text-center mb-4" tag="h4" style={{ color: "#8B4513" }}>
              Client Post-Session Survey
            </CardTitle>

            <div className="mb-4">
              <p className="mb-2">The therapist made me feel comfortable</p>
              <Row>
                <Col xs="6">
                  <Button
                    block
                    color={therapistComfortable === "YES" ? "primary" : "light"}
                    onClick={() => {
                      setTherapistComfortable("YES");
                      setShowContactMessage(false); // Ẩn thông báo khi chọn YES
                    }}
                    className="mb-2"
                  >
                    YES
                  </Button>
                </Col>
                <Col xs="6">
                  <Button
                    block
                    color={therapistComfortable === "NO" ? "primary" : "light"}
                    onClick={() => {
                      setTherapistComfortable("NO");
                      setShowContactMessage(false); // Ẩn thông báo khi chọn NO
                    }}
                    className="mb-2"
                  >
                    NO
                  </Button>
                </Col>
              </Row>
            </div>

            <div className="mb-4">
              <p className="mb-2">I am satisfied with my session today</p>
              <Row>
                <Col xs="6">
                  <Button
                    block
                    color={satisfied === "YES" ? "primary" : "light"}
                    onClick={() => {
                      setSatisfied("YES");
                      setShowContactMessage(false); // Ẩn thông báo khi người dùng chọn YES
                    }}
                    className="mb-2"
                  >
                    YES
                  </Button>
                </Col>
                <Col xs="6">
                  <Button
                    block
                    color={satisfied === "NO" ? "primary" : "light"}
                    onClick={() => {
                      setSatisfied("NO");
                      setShowContactMessage(false); // Ẩn thông báo khi người dùng chọn NO
                    }}
                    className="mb-2"
                  >
                    NO
                  </Button>
                </Col>
              </Row>
              <Button
                block
                color={satisfied === "CONTACT" ? "primary" : "light"}
                onClick={() => {
                  setSatisfied("CONTACT");
                  setShowContactMessage(true);
                }}
                className="mb-2"
                style={satisfied === "CONTACT" ? { backgroundColor: "#F47B20", borderColor: "#F47B20", color: "white" } : {}}
              >
                {satisfied === "CONTACT" && <span style={{ marginRight: "10px" }}>✓</span>}
                NO, please contact me
              </Button>

              {/* Hiển thị thông báo xác nhận khi người dùng chọn 'NO, please contact me' */}
              {showContactMessage && (
                <div className="mt-2 p-3" style={{
                  backgroundColor: "#FFF8F0",
                  borderLeft: "4px solid #F47B20",
                  borderRadius: "4px"
                }}>
                  <p className="mb-0" style={{ color: "#4A4A4A" }}>
                    Thank you for letting us know. Our Clinical Director has been informed and will personally reach out to you soon to discuss your concerns and provide any assistance needed.
                  </p>
                </div>
              )}
            </div>

            <div className="mb-4">
              <p className="mb-2">My next session has been scheduled</p>
              <Row>
                <Col xs="6">
                  <Button
                    block
                    color={nextSessionScheduled === "YES" ? "primary" : "light"}
                    onClick={() => {
                      setNextSessionScheduled("YES");
                      // setShowContactMessage(false); // Ẩn thông báo khi chọn YES
                    }}
                    className="mb-2"
                    style={nextSessionScheduled === "YES" ? { backgroundColor: "#F47B20", borderColor: "#F47B20", color: "white" } : {}}
                  >
                    {nextSessionScheduled === "YES" && <span style={{ marginRight: "10px" }}>✓</span>}
                    YES
                  </Button>
                </Col>
                <Col xs="6">
                  <Button
                    block
                    color={nextSessionScheduled === "NO" ? "primary" : "light"}
                    onClick={() => {
                      setNextSessionScheduled("NO");
                      // setShowContactMessage(false); // Ẩn thông báo khi chọn NO
                    }}
                    className="mb-2"
                    style={nextSessionScheduled === "NO" ? { backgroundColor: "#F47B20", borderColor: "#F47B20", color: "white" } : {}}
                  >
                    {nextSessionScheduled === "NO" && <span style={{ marginRight: "10px" }}>✓</span>}
                    NO
                  </Button>
                </Col>
              </Row>

              {/* Hiển thị lịch nếu người dùng chọn "NO" */}
              {nextSessionScheduled === "NO" && renderCalendar()}
            </div>

            <div className="mb-4">
              <p className="text-center mb-3">Rate your session</p>
              <div className="d-flex justify-content-center mb-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    onClick={() => handleRating(star)}
                    style={{
                      cursor: "pointer",
                      fontSize: "28px",
                      color: star <= rating ? "#F4C542" : "#D3D3D3",
                      margin: "0 5px"
                    }}
                  >
                    ★
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <Input
                type="textarea"
                placeholder="How was your session today?"
                value={feedback}
                onChange={handleFeedbackChange}
                rows={3}
                className="mb-2"
              />
            </div>

            <Button
              block
              color="primary"
              onClick={handleSubmitFeedback}
              className="mb-2"
              style={isHovered ? submitButtonHoverStyle : submitButtonStyle}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              Submit Feedback
            </Button>

            <Button
              block
              color="light"
              onClick={() => props.onClose(null, true)}
              className="text-muted"
              style={{
                fontSize: "14px",
                fontWeight: "normal",
                padding: "8px",
                backgroundColor: "#f8f9fa",
                borderColor: "#f8f9fa",
                color: "#adb5bd"
              }}
            >
              Skip
            </Button>
          </CardBody>
        </Card>
      </ModalBody>
    </Modal>
  );
};

export default ClientPostSessionSurvey;